"use client"

import * as React from "react"
import * as AvatarPrimitive from "@radix-ui/react-avatar"
import Image from "next/image"

import { cn } from "@/lib/utils"

// Context to communicate image load status to fallback
const AvatarLoadContext = React.createContext<{
  hasLoaded: boolean
  setHasLoaded: (loaded: boolean) => void
} | null>(null)

function Avatar({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root>) {
  const [hasLoaded, setHasLoaded] = React.useState(false)

  return (
    <AvatarLoadContext.Provider value={{ hasLoaded, setHasLoaded }}>
      <AvatarPrimitive.Root
        data-slot="avatar"
        className={cn(
          "relative flex size-8 shrink-0 overflow-hidden rounded-full",
          className
        )}
        {...props}
      >
        {children}
      </AvatarPrimitive.Root>
    </AvatarLoadContext.Provider>
  )
}

interface AvatarImageProps {
  src?: string | null
  alt?: string
  className?: string
}

function AvatarImage({
  className,
  src,
  alt = "",
}: AvatarImageProps) {
  const [hasError, setHasError] = React.useState(false)
  const context = React.useContext(AvatarLoadContext)

  // Reset error state when src changes
  React.useEffect(() => {
    setHasError(false)
    context?.setHasLoaded(false)
  }, [src, context])

  // If no src or error, don't render (will show fallback)
  if (!src || hasError) {
    return null
  }

  return (
    <Image
      data-slot="avatar-image"
      className={cn("aspect-square size-full object-cover", className)}
      src={src}
      alt={alt}
      fill
      sizes="(max-width: 768px) 64px, 128px"
      onLoad={() => context?.setHasLoaded(true)}
      onError={() => setHasError(true)}
    />
  )
}

interface AvatarFallbackProps extends React.HTMLAttributes<HTMLDivElement> {
  delayMs?: number
}

function AvatarFallback({
  className,
  delayMs = 0,
  children,
  ...props
}: AvatarFallbackProps) {
  const context = React.useContext(AvatarLoadContext)
  const [showFallback, setShowFallback] = React.useState(delayMs === 0)

  React.useEffect(() => {
    if (delayMs > 0) {
      const timer = setTimeout(() => setShowFallback(true), delayMs)
      return () => clearTimeout(timer)
    }
  }, [delayMs])

  // Don't show fallback if image has loaded
  if (context?.hasLoaded) {
    return null
  }

  // Don't show fallback until delay has passed
  if (!showFallback) {
    return null
  }

  return (
    <div
      data-slot="avatar-fallback"
      className={cn(
        "bg-muted flex size-full items-center justify-center rounded-full",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export { Avatar, AvatarImage, AvatarFallback }
